/**
 * Formatting helpers shared by every renewals page.
 *
 * Port of the Spark renewals app's ``frontend/src/format.js`` — keep these
 * compatible so visually-identical numbers come through end-to-end.
 */

export function fmtCur(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v === 0) return "—";
  if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toFixed(2) + "M";
  if (Math.abs(v) >= 10_000) return "$" + (v / 1_000).toFixed(1) + "K";
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * Exact per-unit money — reconciliation surfaces where sub-cent precision
 * matters (e.g. SMS per-credit pricing). 2–6 decimals: always at least cents,
 * up to 6 dp, trailing zeros beyond cents trimmed automatically. Coerces via
 * Number() because Cinch's DRF DecimalFields may serialize as strings.
 */
export function fmtCurExact(n: number | string | null | undefined): string {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

/** Money to exactly 2 decimals — headline/aggregate figures being diffed against
 *  Cinch/HubSpot (line totals, subtotal, base fee, total contract value, MRR/ARR).
 *  No $K/$M compaction, so the number is exact and reconcilable. Renders a real 0
 *  as "$0.00" (an explicit zero is a value when reconciling), only null/NaN as "—". */
export function fmtCur2(n: number | string | null | undefined): string {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtPct(n: number | null | undefined): string {
  if (n == null) return "—";
  return Number(n).toFixed(1) + "%";
}

export function monthLabel(ym: string | null | undefined): string {
  if (!ym) return "—";
  const s = String(ym).substring(0, 7);
  const [y, m] = s.split("-");
  if (!y || !m) return s;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const idx = Number.parseInt(m, 10) - 1;
  return (months[idx] ?? m) + " " + y;
}

/** Compact date — for YYYY-MM returns "Mon YYYY", for YYYY-MM-DD returns
 *  "Mon DD". Falls back to the input on parse failure. */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const m = String(s).match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!m) return String(s);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const monthIdx = Number.parseInt(m[2] ?? "0", 10) - 1;
  const monthName = months[monthIdx] ?? m[2];
  if (m[3]) return `${monthName} ${Number.parseInt(m[3], 10)}, ${m[1]}`;
  return `${monthName} ${m[1]}`;
}

export function fmtMRR(n: number | null | undefined): string {
  if (n == null) return "—";
  const v = Number(n);
  if (!v) return "$0";
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function relativeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const ms = Date.now() - t;
  const s = Math.floor(ms / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
